import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ImageContainer, StyledList } from '@fuww/library/src/Article';
import {
  NewsFeedListItemWrapper,
  NewsFeedItem,
  SearchListItemWrapper,
  SearchResultsItem,
} from '@fuww/library/src/List/NewsList';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import { useIntl } from 'react-intl';
import { capitalize } from 'lodash';
import { RelativeContainer } from '@fuww/library/src/Card';
import LinkAndAnchor from '../../LinkAndAnchor';
import getArticlesParameters from '../../../lib/getArticlesParameters';
import ImageWithUrls from '../../ImageWithUrls';
import FormattedDate from '../../FormattedDate';
import getCategoryTitle from '../../../lib/getCategoryTitle';
import getProxiedImages from '../../../lib/getProxiedImages';
import { useSiteContext } from '../../SiteContext';
import LoadMore from '../../LoadMore';
import loadMore from '../../../lib/queries/loadMore';

const getSearchListLabels = (labels) => {
  if (!labels || labels.length === 0) {
    return '';
  }
  return labels.map((label) => ` | ${capitalize(label)}`);
};

export const SearchListItem = ({
  article,
  isFirstImage,
  showLoader,
}) => {
  const intl = useIntl();
  const { hasLocalNews } = useSiteContext();

  const {
    title,
    imageUrls,
    category,
    insertedAt,
    path,
    labels,
    pressRelease,
    partnerContent,
  } = article;
  const fetchpriority = isFirstImage ? 'high' : 'low';

  return (
    <SearchListItemWrapper className="fu-list-item-content">
      <LinkAndAnchor
        {...showLoader ? {
          href: '#',
        } : {
          route: 'article',
          params: getArticlesParameters(article),
          useRouter: true,
          href: path,
        }}
      >
        <SearchResultsItem
          article={article}
          date={<FormattedDate value={insertedAt} />}
          category={
              category && ` - ${
                getCategoryTitle(category, hasLocalNews, intl)
              }${getSearchListLabels(labels)}`
            }
          isPressRelease={pressRelease}
          isPartnerContent={partnerContent}
          image={imageUrls && imageUrls[0] && (
          <ImageContainer>
            <ImageWithUrls
              imageUrls={imageUrls}
              alt={title}
              width="102px"
              height="auto"
              sizes="100px"
              objectFit="cover"
              srcSet={`${imageUrls[1]
              } 102w, ${imageUrls[2]
              } 204w, ${imageUrls[3]
              } 306w`}
              preload={isFirstImage}
              finalFetchpriority={fetchpriority}
              placeholderFetchpriority={fetchpriority}
            />
          </ImageContainer>
          )}
        />
      </LinkAndAnchor>
    </SearchListItemWrapper>
  );
};

SearchListItem.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    imageUrls: PropTypes.arrayOf(PropTypes.string),
    category: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      path: PropTypes.string,
    }),
    insertedAt: PropTypes.string,
    path: PropTypes.string,
    labels: PropTypes.arrayOf(
      PropTypes.string,
    ),
    pressRelease: PropTypes.bool,
    partnerContent: PropTypes.bool,
  }),
  isFirstImage: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool,
};

SearchListItem.defaultProps = {
  article: {},
  showLoader: false,
};

export const NewsFeedListItem = ({
  article,
  isFirstImage,
  variables,
  showLoader,
}) => {
  const intl = useIntl();
  const { hasLocalNews } = useSiteContext();

  const {
    title,
    imageUrls: originalImageUrls,
    category,
    insertedAt,
    path,
    pressRelease,
    partnerContent,
  } = { ...article };

  const { imageUrls, srcSet } = showLoader ? {} : getProxiedImages(
    variables, originalImageUrls,
  );
  const fetchpriority = isFirstImage ? 'high' : 'low';

  return (
    <NewsFeedListItemWrapper className="fu-list-item-content">
      <LinkAndAnchor
        {...showLoader ? {
          href: '#',
        } : {
          route: 'article',
          params: getArticlesParameters(article),
          useRouter: true,
          href: path,
        }}

      >
        <NewsFeedItem
          article={article}
          date={<FormattedDate value={insertedAt} />}
          category={category && getCategoryTitle(
            category, hasLocalNews, intl,
          )}
          isPressRelease={pressRelease}
          isPartnerContent={partnerContent}
          image={imageUrls && imageUrls[0] && title && (
            <RelativeContainer>
              <ImageWithUrls
                imageUrls={imageUrls}
                alt={title}
                width="100%"
                height="auto"
                sizes={`
                (min-width: ${breakpoints.sm}px) 201px,
                36vw
              `}
                objectFit="cover"
                srcSet={srcSet}
                preload={isFirstImage}
                finalFetchpriority={fetchpriority}
                placeholderFetchpriority={fetchpriority}
              />
            </RelativeContainer>
          )}
          showLoader={showLoader}
        />
      </LinkAndAnchor>
    </NewsFeedListItemWrapper>
  );
};

NewsFeedListItem.propTypes = {
  variables: PropTypes.shape({}).isRequired,
  article: PropTypes.shape({
    category: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      path: PropTypes.string,
    }),
    pressRelease: PropTypes.bool,
    partnerContent: PropTypes.bool,
    insertedAt: PropTypes.string,
    imageUrls: PropTypes.arrayOf(PropTypes.string),
    path: PropTypes.string,
    title: PropTypes.string,
  }),
  isFirstImage: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool,
};

NewsFeedListItem.defaultProps = {
  showLoader: false,
  article: {},
};

const ArticleList = ({
  ArticleListItem,
  variables,
  fetchMore,
  field,
  connectionField,
  data,
  loading,
  showLoader,
}) => {
  const { edges, pageInfo: { hasNextPage } } = {
    edges: [],
    pageInfo: { hasNextPage: false },
    ...data[field],
    ...data[connectionField]?.[field],
  };
  return (
    <>
      <StyledList className="fu-mdc-list">
        {edges.length > 0 ? (edges.map((edge, index) => {
          const { node } = { ...edge };

          return (
            <ArticleListItem
              article={node}
              isPressRelease={node?.pressRelease}
              isPartnerContent={node?.partnerContent}
              variables={variables}
              isFirstImage={index === 0}
              key={node?.id?.toString()}
              showLoader={showLoader}
            />
          );
        })) : (showLoader ? (Array.from({ length: 4 }, (_, index) => (
          <Fragment key={`skeleton-${index}`}>
            <ArticleListItem
              showLoader
              key={`skeleton-${index}`}
              isPressRelease={false}
              isPartnerContent={false}
              variables={{}}
              isFirstImage={false}
            />
          </Fragment>
        ))) : null)}
      </StyledList>
      <LoadMore
        loading={loading}
        hasNextPage={hasNextPage}
        fetchMore={
          () => loadMore({
            variables,
          })(data, field, fetchMore, connectionField)
        }
      />
    </>
  );
};

ArticleList.propTypes = {
  ArticleListItem: PropTypes.elementType,
  variables: PropTypes.shape({}),
  data: PropTypes.shape({}),
  fetchMore: PropTypes.func,
  field: PropTypes.string.isRequired,
  connectionField: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool.isRequired,
};

ArticleList.defaultProps = {
  ArticleListItem: SearchListItem,
  variables: null,
  data: {},
  fetchMore: undefined,
  connectionField: null,
};

export default ArticleList;
